import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../api/apiService';
import type { Training } from '../../types';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Clock, Calendar, Play } from 'lucide-react';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import './MyTrainings.css';

export const MyTrainings: React.FC = () => {
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    loadTrainings();
  }, []);

  const loadTrainings = async () => {
    try {
      const data = await apiService.getTrainings();
      // Sort trainings by date (most recent first)
      setTrainings(data.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
    } catch (error) {
      console.error('Error loading trainings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateEstimatedTime = (training: Training): number => {
    let totalSeconds = 0;

    // Warmup section
    if (training.warmup_section?.exercises && training.warmup_section.exercises.length > 0) {
      const count = training.warmup_section.exercises.length;
      const seconds = training.warmup_section.params?.seconds || 0;
      const rest = training.warmup_section.params?.rest_between_exercises || 0;

      totalSeconds += count * seconds;
      totalSeconds += (count - 1) * rest;
    }

    // Main section
    if (training.main_section?.exercises && training.main_section.exercises.length > 0) {
      const count = training.main_section.exercises.length;
      const series = training.main_section.params?.series || 1;
      const seconds = training.main_section.params?.seconds || 0;
      const restBetweenSeries = training.main_section.params?.rest_between_series || 0;
      const restBetweenExercises = training.main_section.params?.rest_between_exercises || 0;

      totalSeconds += count * series * seconds;
      totalSeconds += count * (series - 1) * restBetweenSeries;
      totalSeconds += (count - 1) * restBetweenExercises;
    }

    // Cooldown section
    if (training.cooldown_section?.exercises && training.cooldown_section.exercises.length > 0) {
      const count = training.cooldown_section.exercises.length;
      const seconds = training.cooldown_section.params?.seconds || 0;
      const rest = training.cooldown_section.params?.rest_between_exercises || 0;

      totalSeconds += count * seconds;
      totalSeconds += (count - 1) * rest;
    }

    return totalSeconds;
  };

  const formatTime = (totalSeconds: number): string => {
    if (totalSeconds === 0) return '0s';

    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    if (minutes > 0 && seconds > 0) {
      return `${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    } else {
      return `${seconds}s`;
    }
  };

  if (isLoading) {
    return <div className="text-center p-4">{t('loading')}</div>;
  }

  return (
    <div className="my-trainings-page">
      <div className="my-trainings-header">
        <h1 className="my-trainings-title">{t('myTrainings')}</h1>
        <p className="my-trainings-subtitle">{t('consultYourScheduledTrainings')}</p>
      </div>

      {trainings.length === 0 ? (
        <Card>
          <p className="no-trainings">{t('noTrainingsAssigned')}</p>
        </Card>
      ) : (
        <div className="trainings-list-container">
          {trainings.map((training) => {
            const estimatedTime = calculateEstimatedTime(training);
            return (
              <Card key={training.id} className="training-item-card">
                <div className="training-item-header">
                  <div className="training-item-header-content">
                    <h2 className="training-item-title">{training.title}</h2>
                    <div className="training-item-meta">
                      <span className="training-item-date">
                        <Calendar size={16} />
                        {format(new Date(training.date), i18n.language === 'es' ? "d 'de' MMMM, yyyy" : 'MMMM d, yyyy', {
                          locale: i18n.language === 'es' ? es : enUS
                        })}
                      </span>
                      <span className="training-item-time">
                        <Clock size={16} />
                        {format(new Date(training.date), 'HH:mm')}
                      </span>
                      {estimatedTime > 0 && (
                        <span className="training-item-duration">
                          <Clock size={16} />
                          {formatTime(estimatedTime)}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate(`/user/training/run/${training.id}`)}
                    className="start-training-button-header"
                    variant="primary"
                  >
                    <Play size={18} />
                    {t('startTraining')}
                  </Button>
                </div>

              {training.observations && (
                <p className="training-item-description">{training.observations}</p>
              )}

              <div className="training-sections-grid">
                {/* Warmup Section */}
                {training.warmup_section && training.warmup_section.exercises.length > 0 && (
                  <div className="training-section-column warmup">
                    <div className="section-badge warmup-badge">{t('warmup')}</div>
                    <ul className="section-exercises-list">
                      {training.warmup_section.exercises.map((exercise, idx) => (
                        <li key={idx} className="section-exercise-item">
                          • {exercise.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Main Section */}
                {training.main_section && training.main_section.exercises.length > 0 && (
                  <div className="training-section-column main">
                    <div className="section-badge main-badge">{t('mainPart')}</div>

                    {/* Parameters for main section */}
                    {(training.main_section.params.series ||
                      training.main_section.params.seconds ||
                      training.main_section.params.rest_between_series ||
                      training.main_section.params.rest_between_exercises) && (
                      <div className="section-params">
                        {training.main_section.params.series && (
                          <span className="param-item">
                            <strong>Series:</strong> {training.main_section.params.series}
                          </span>
                        )}
                        {training.main_section.params.seconds && (
                          <span className="param-item">
                            <strong>Tiempo:</strong> {training.main_section.params.seconds}s
                          </span>
                        )}
                        {training.main_section.params.rest_between_series && (
                          <span className="param-item">
                            <strong>Desc. series:</strong> {training.main_section.params.rest_between_series}s
                          </span>
                        )}
                        {training.main_section.params.rest_between_exercises && (
                          <span className="param-item">
                            <strong>Desc. ejercicios:</strong> {training.main_section.params.rest_between_exercises}s
                          </span>
                        )}
                      </div>
                    )}

                    <ul className="section-exercises-list">
                      {training.main_section.exercises.map((exercise, idx) => (
                        <li key={idx} className="section-exercise-item">
                          • {exercise.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Cooldown Section */}
                {training.cooldown_section && training.cooldown_section.exercises.length > 0 && (
                  <div className="training-section-column cooldown">
                    <div className="section-badge cooldown-badge">{t('cooldown')}</div>
                    <ul className="section-exercises-list">
                      {training.cooldown_section.exercises.map((exercise, idx) => (
                        <li key={idx} className="section-exercise-item">
                          • {exercise.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
